/**
 * [TD-47.5 Gap 5.A] PickHistoryTabBody - refactored from PickHistorySidebar.
 *
 * Drops:
 *   • outer card chrome (`rounded-2xl border p-3`) - tab body lives inside
 *     TabbedPanel's container, double-chrome would noise the surface
 *   • <h3>Pick History</h3> header - tab strip already says "History"
 *
 * Preserves:
 *   • Reverse-chronological grouping by round
 *   • voiceByPickRequestId merge for ACTIVITY_WRITTEN voice_text enrichment
 *   • Stable React key handling (RL-30) - request_id preferred, compound fallback
 *   • PositionBadge + ManagerName + ActivitySpeakerButton rendering
 *   • Empty state copy ("No picks yet.")
 *   • Same prop signature as PickHistorySidebar so consumers swap one-line
 */
import React, { useMemo } from "react";
import PositionBadge from "../PositionBadge";
import ManagerName from "../ManagerName";
import ActivitySpeakerButton from "../activity/ActivitySpeakerButton";

export default function PickHistoryTabBody({ draftState, fullHistory, activityVoice, isDark }) {
  // [Founder reconnect-parity - Feb 2026] Prefer server-hydrated
  // `fullHistory` (unbounded); fall back to snake-style `pick_history`
  // for snake drafts, then to auction's WS-trimmed `completed_sales`
  // (last 20) as a final safety net.
  const picks =
    (Array.isArray(fullHistory) && fullHistory.length > 0 && fullHistory) ||
    draftState?.pick_history ||
    draftState?.completed_sales ||
    [];
  const text = isDark ? "#F5F5F5" : "#0A0A0A";
  const muted = isDark ? "#888" : "#666";

  const grouped = useMemo(() => {
    const m = new Map();
    for (let i = picks.length - 1; i >= 0; i--) {
      const p = picks[i];
      const r = p.round;
      if (!m.has(r)) m.set(r, []);
      m.get(r).push(p);
    }
    return Array.from(m.entries());
  }, [picks]);

  const voiceByPickRequestId = useMemo(() => {
    const map = {};
    Object.entries(activityVoice || {}).forEach(([activityId, v]) => {
      if (v?.pickId) map[v.pickId] = { activityId, ...v };
    });
    return map;
  }, [activityVoice]);

  return (
    <div
      className="h-full overflow-y-auto pr-1 space-y-3"
      data-testid="pick-history-tab-body"
    >
      {grouped.length === 0 && (
        <p className="text-xs px-1 pt-2" style={{ color: muted }}>
          No picks yet.
        </p>
      )}
      {grouped.map(([roundNum, roundPicks]) => (
        <div key={roundNum}>
          <p
            className="font-display font-black text-[9px] uppercase tracking-widest mb-1.5"
            style={{ color: muted }}
          >
            Round {roundNum}
          </p>
          <div className="space-y-1.5">
            {roundPicks.map((p, idx) => {
              const team = draftState?.managers?.[p.team_id];
              const enriched =
                voiceByPickRequestId[p.request_id] ||
                voiceByPickRequestId[p.overall_pick];
              const stableKey =
                p.request_id ||
                `${p.overall_pick}-${p.player?.id || idx}`;
              return (
                <div
                  key={stableKey}
                  className="flex items-center gap-2 text-xs"
                  data-testid={`pick-history-row-${p.overall_pick}`}
                >
                  <span
                    className="font-mono text-[9px] tabular-nums w-6 shrink-0"
                    style={{ color: muted }}
                  >
                    {p.overall_pick}.
                  </span>
                  <PositionBadge position={p.player?.position} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p
                      className="font-display font-bold truncate"
                      style={{ color: text }}
                    >
                      {p.player?.name}
                    </p>
                    <p className="text-[10px] truncate" style={{ color: muted }}>
                      <ManagerName
                        name={team?.team_name || team?.first_name}
                        isBot={!!team?.is_bot}
                        iconSize={9}
                        testId={`pick-team-${p.overall_pick}`}
                      />
                    </p>
                    {enriched?.voiceText && (
                      <div className="flex items-start gap-1.5 mt-0.5">
                        <p
                          className="text-[10px] italic flex-1"
                          style={{ color: muted }}
                        >
                          "{enriched.voiceText}"
                        </p>
                        <ActivitySpeakerButton
                          activityId={enriched.activityId}
                          voiceText={enriched.voiceText}
                          voiceSource={enriched.voiceSource}
                          testid={`pick-speaker-${p.overall_pick}`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
